import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mic, Save, CheckCircle2, XCircle, ExternalLink, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [formData, setFormData] = useState({
    elevenlabs_api_key: "",
    elevenlabs_agent_id: "",
    elevenlabs_webhook_secret: "",
    elevenlabs_phone_number_id: "",
  });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Einstellungen");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = {};
      if (formData.elevenlabs_api_key) dataToSave.elevenlabs_api_key = formData.elevenlabs_api_key;
      if (formData.elevenlabs_agent_id) dataToSave.elevenlabs_agent_id = formData.elevenlabs_agent_id;
      if (formData.elevenlabs_webhook_secret) dataToSave.elevenlabs_webhook_secret = formData.elevenlabs_webhook_secret;
      if (formData.elevenlabs_phone_number_id) dataToSave.elevenlabs_phone_number_id = formData.elevenlabs_phone_number_id;
      await axios.put(`${API}/settings`, dataToSave);
      toast.success("Einstellungen gespeichert");
      fetchSettings();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="h-64 bg-slate-200 animate-pulse rounded-xl" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Admin-Einstellungen</h1>
        <p className="text-slate-500 mt-1">Globale KI-Konfiguration für alle Nutzer</p>
      </div>

      <Card className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                <Mic className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">ElevenLabs Conversational AI</CardTitle>
                <CardDescription>KI-Agent für echte Gespräche — gilt für alle Nutzer</CardDescription>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className={`flex items-center gap-2 text-sm ${settings?.elevenlabs_configured ? 'text-emerald-600' : 'text-slate-400'}`}>
                {settings?.elevenlabs_configured ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                <span>{settings?.elevenlabs_configured ? "Konfiguriert" : "Nicht konfiguriert"}</span>
              </div>
              {settings?.elevenlabs_agent_configured && (
                <span className="text-xs text-emerald-600">Agent aktiv</span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>API Key</Label>
              <div className="relative">
                <Input
                  type={showKey ? "text" : "password"}
                  value={formData.elevenlabs_api_key}
                  onChange={(e) => setFormData({ ...formData, elevenlabs_api_key: e.target.value })}
                  placeholder="••••••••••••••••"
                />
                <button type="button" onClick={() => setShowKey(!showKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Agent ID</Label>
              <Input
                value={formData.elevenlabs_agent_id}
                onChange={(e) => setFormData({ ...formData, elevenlabs_agent_id: e.target.value })}
                placeholder="agent_xxxxxxxxxxxxx"
              />
              <p className="text-xs text-slate-400">Von ElevenLabs Conversational AI</p>
            </div>
            <div className="space-y-2">
              <Label>Phone Number ID</Label>
              <Input
                value={formData.elevenlabs_phone_number_id}
                onChange={(e) => setFormData({ ...formData, elevenlabs_phone_number_id: e.target.value })}
                placeholder="pn_xxxxxxxxxxxxx"
              />
              <p className="text-xs text-slate-400">Für ausgehende Anrufe via ElevenLabs</p>
            </div>
            <div className="space-y-2">
              <Label>Webhook Secret (optional)</Label>
              <div className="relative">
                <Input
                  type={showSecret ? "text" : "password"}
                  value={formData.elevenlabs_webhook_secret}
                  onChange={(e) => setFormData({ ...formData, elevenlabs_webhook_secret: e.target.value })}
                  placeholder="wsec_••••••••••••••••"
                />
                <button type="button" onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-blue-800">
              <strong>Webhook URL für ElevenLabs:</strong><br />
              <code className="text-xs bg-blue-100 px-1 py-0.5 rounded">
                {window.location.origin}/api/elevenlabs/webhook
              </code>
            </p>
          </div>
          <div className="flex items-center justify-between pt-4 border-t border-slate-100">
            <a href="https://elevenlabs.io/app/conversational-ai" target="_blank" rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              ElevenLabs Agents öffnen <ExternalLink className="w-3 h-3" />
            </a>
            <Button onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
              <Save className="w-4 h-4 mr-2" />
              Speichern
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
